import React, { useState, useEffect, useRef, useCallback } from 'react';
import { client } from '../client';
import { motion } from 'framer-motion';
import imageUrlBuilder from '@sanity/image-url';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';

const builder = imageUrlBuilder(client);

const urlFor = (source) => {
  if (!source) return null;
  try {
    return builder.image(source).url();
  } catch (err) {
    return null;
  }
};

export default function Prints() {
  const [prints, setPrints] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(null);

  const theme = useTheme();
  const darkMode = theme?.darkMode ?? true;

  useEffect(() => {
    client
      .fetch(`*[_type == "print"] | order(orderNumber asc)`)
      .then((data) => {
        setPrints(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setPrints([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    document.body.style.overflow =
      selectedIndex !== null ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedIndex]);

  const total = prints.length;

  const goNext = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === null || total === 0 ? 0 : prev === total - 1 ? 0 : prev + 1
    );
  }, [total]);

  const goPrev = useCallback(() => {
    setSelectedIndex((prev) =>
      prev === null || total === 0 ? 0 : prev === 0 ? total - 1 : prev - 1
    );
  }, [total]);

  const closeModal = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const openModal = (i) => setSelectedIndex(i);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, goNext, goPrev, closeModal]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > 50) {
      deltaX < 0 ? goNext() : goPrev();
    }
    touchStartX.current = null;
  };

  if (loading) {
    return (
      <main
        style={{
          background: darkMode
            ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
            : '#ffffff',
        }}
        className="min-h-screen"
      />
    );
  }

  return (
    <main
      style={{
        background: darkMode
          ? 'linear-gradient(to top right, #1e1b4b, #020617, #000000)'
          : '#ffffff',
        color: darkMode ? '#ffffff' : '#0f172a',
        transition: 'all 0.3s ease',
      }}
      className="min-h-screen w-full flex flex-col overflow-x-hidden"
    >
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-10 md:pt-16 pb-10 w-full">
        <h2 className={`h1 text-left font-bold mb-5 sm:text-7xl ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          Available Prints
        </h2>
        <p className={`max-w-xl text-left ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Selected photographic works available as collectible prints. Prints will be ready in 7 days.
        </p>
      </section>

      {/* EMPTY STATE */}
      {!prints.length && (
        <div className="flex-1 flex items-center justify-center px-6">
          <p className={`text-sm tracking-wide ${darkMode ? 'text-white/60' : 'text-slate-400'}`}>
            No prints available yet.
          </p>
        </div>
      )}

      {/* GRID */}
      {prints.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-20 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {prints.map((item, index) => (
              <motion.div
                key={item._id || index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
                viewport={{ once: true }}
                className={`border rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  darkMode
                    ? 'bg-white/[0.03] border-white/10 hover:border-white/20'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-400'
                }`}
                onClick={() => openModal(index)}
              >
                <div className={`flex items-center justify-center p-3 ${darkMode ? 'bg-black/20' : 'bg-slate-100'}`}>
                  {urlFor(item.image) ? (
                    <img
                      src={urlFor(item.image)}
                      alt="print"
                      className="w-full h-auto max-h-[500px] object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className={`py-20 text-sm ${darkMode ? 'text-white/40' : 'text-slate-400'}`}>
                      No image
                    </div>
                  )}
                </div>
                <div className="p-4 flex flex-col gap-2">
                  {item.price && (
                    <p className={`text-sm tracking-wide ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      ETB {item.price}
                    </p>
                  )}
                  {item.sizes && (
                    <p className={`text-xs tracking-wide uppercase ${darkMode ? 'text-white/50' : 'text-slate-400'}`}>
                      {item.sizes.join(' · ')}
                    </p>
                  )}
                  {item.isLimitedEdition && (
                    <p className="text-xs text-sky-300 tracking-[0.2em] uppercase pt-1">
                      Limited Edition
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* MODAL */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* X button — mobile only */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 z-20 text-white text-3xl font-light sm:hidden"
            aria-label="Close"
          >
            ✕
          </button>

          {/* Arrows — desktop only */}
          <button
            onClick={goPrev}
            className="absolute left-4 z-20 text-white text-6xl font-extralight px-6 py-4 hidden sm:block"
          >
            ‹
          </button>

          <motion.div
            key={selectedIndex}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative z-10 max-w-2xl w-full px-6 flex flex-col items-center gap-4"
          >
            {urlFor(prints[selectedIndex]?.image) && (
              <img
                src={urlFor(prints[selectedIndex].image)}
                alt="print-full"
                className="w-full max-h-[75vh] object-contain"
              />
            )}

            {/* Card details */}
            <div className="flex flex-col items-center gap-1 text-center">
              {prints[selectedIndex]?.price && (
                <p className="text-sm tracking-wide text-white">
                  ETB {prints[selectedIndex].price}
                </p>
              )}
              {prints[selectedIndex]?.sizes && (
                <p className="text-xs tracking-wide uppercase text-white/50">
                  {prints[selectedIndex].sizes.join(' · ')}
                </p>
              )}
              {prints[selectedIndex]?.isLimitedEdition && (
                <p className="text-xs text-sky-300 tracking-[0.2em] uppercase pt-1">
                  Limited Edition
                </p>
              )}
            </div>
          </motion.div>

          <button
            onClick={goNext}
            className="absolute right-4 z-20 text-white text-6xl font-extralight px-6 py-4 hidden sm:block"
          >
            ›
          </button>
        </div>
      )}

      <Footer />
    </main>
  );
}